import React, { useCallback, useEffect } from 'react';
import FrontDoorsPageContainer from '@strava/front-doors-page-container-ui';
import {
  AnalyticsFieldsTypes,
  EMAIL,
  EmailRegistrationFormProps
} from '@strava/authentication-ui';
import {
  useStravaAnalytics,
  useStravaErrorLogging,
  useStravaBranch
} from '@strava/container-context';
import { useApple } from '@strava/utils/src/externalSocialPartners/useApple';
import { setFullScreenBackgroundImage } from '@strava/utils/src/utils';
import DesktopRegistrationOptions from './components/DesktopRegistrationOptions';
import DesktopRegistrationMessage, {
  DesktopRegistrationMessageProps
} from './components/DesktopRegistrationMessage';
import MobileRegistrationOptions from './components/MobileRegistrationOptions';
import MobileRegistrationMessage from './components/MobileRegistrationMessage';
import { useExtAuthBtnClickHandler } from './helpers/useExtAuthBtnClickHandler.hook';
import { matchMedia } from '@strava/ui/MediaQuery';
import clsx from '@strava/ui/clsx';
import styles from './AccountRegistrationPage.module.scss';

export type AccountRegistrationPageProps = EmailRegistrationFormProps & {
  registrationMessage: DesktopRegistrationMessageProps;
  authorizationUrls: {
    email: string;
    facebook: string;
    google: string;
  };
  background: `background-${string}`; // where string is a number ('background-3')
  locale: string;
  denyCookies: boolean;
  dataSharingOptedOut: boolean;
  userAgent: string;
  appleState: string;
  cohort?: string | undefined;
  flashMessage?: string | undefined;
  showUpdatedMobileStyling?: boolean;
};

const AccountRegistrationPage = ({
  registrationMessage,
  background,
  locale,
  denyCookies,
  dataSharingOptedOut,
  authorizationUrls,
  userAgent,
  appleState,
  showUpdatedMobileStyling = false,
  ...regOptionsProps
}: AccountRegistrationPageProps) => {
  const { track: _track } = useStravaAnalytics();
  const { logError } = useStravaErrorLogging();
  const { signin: appleSignin } = useApple(appleState, logError);

  useEffect(() => {
    if (!matchMedia.screenXsMax()) {
      (async () => {
        try {
          /**
           * webpack comments ensure that the background images are included
           * with the current chunk, which prevents `active` from making a forbidden
           * request to cloudfront
           */
          await import(
            /* webpackMode: "eager" */
            /* webpackExports: ["default", "named"] */
            `./assets/${background}.jpg`
          ).then((module) => {
            const backgroundImage = module.default;

            // apply background image directly to `<body>`
            setFullScreenBackgroundImage(backgroundImage);
          });
        } catch (error) {
          logError(error);
        }
      })();
    }
  }, [background, logError]);

  const track = useCallback(
    (fields: AnalyticsFieldsTypes) => {
      _track({
        category: 'onboarding',
        page: 'register',
        ...fields,
        properties: {
          background,
          ...fields.properties
        }
      });
    },
    [_track, background]
  );

  useEffect(() => {
    track({ action: 'screen_enter' });
  }, [track]);

  const { handleExtAuthBtnClick } = useExtAuthBtnClickHandler({
    appleSignin,
    authorizationUrls,
    track
  });

  const { isProcessing, createLink, cookiesAccepted } = useStravaBranch();

  // generate branch appLink (deeplink) when the sdk has successfully been
  // initialized.
  useEffect(() => {
    if (!isProcessing) {
      createLink({ feature: 'register' });
    }

    // only listen to cookiesAccepted and isProcessing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookiesAccepted, isProcessing]);

  return (
    <FrontDoorsPageContainer
      locale={locale}
      page="register"
      category="onboarding"
      dataSharingOptedOut={dataSharingOptedOut}
      authorizationUrls={authorizationUrls}
      cpraI18nPrefix="microfrontends.legal" // path to find CPRA translations in active
      menuConfig={{ showLoginOptionOnly: true }}
      className={styles.noBackground}
      userAgent={userAgent}
    >
      <div className={clsx(styles.mobileOnly, styles.mobileRegContainer)}>
        <MobileRegistrationMessage />
        <MobileRegistrationOptions
          track={track}
          emailAuthorizationUrl={authorizationUrls[EMAIL]}
          handleExtAuthBtnClick={handleExtAuthBtnClick}
          showUpdatedMobileStyling={showUpdatedMobileStyling}
          {...regOptionsProps}
        />
      </div>

      <div className={clsx(styles.desktopOnly, styles.desktopRegContainer)}>
        <DesktopRegistrationMessage {...registrationMessage} />
        <DesktopRegistrationOptions
          track={track}
          emailAuthorizationUrl={authorizationUrls[EMAIL]}
          handleExtAuthBtnClick={handleExtAuthBtnClick}
          {...regOptionsProps}
        />
      </div>
    </FrontDoorsPageContainer>
  );
};

export default AccountRegistrationPage;
